import * as React from 'react';
import { connect } from 'react-redux';
import * as _ from 'underscore';

import { TextLinkWidget } from '@tableau/widgets';
import { Formatters, Messages } from '../../../codegen/Localize';
import { FavoriteToggle } from '../../../common/react/components/FavoriteToggle/FavoriteToggle';
import { InterpolatedString } from '../../../common/react/components/InterpolatedString';
import { ProfilePicture } from '../../../common/react/components/ProfilePicture';
import { MetricCardStateProps, MetricDefaultImageUrl, renderMetricCardContent } from '../../../metrics/components/MetricCard';
import { selectMetricDataForContentItem } from '../../../metrics/selectors';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { Helpers } from '../../../ts/helpers/Helpers';
import { ISharedWithMeContentInfo, ISharerInfo, ISharedWithMeCustomViewInfo } from '../../../ts/models/ISharedWithMeContentInfo';
import { FavoriteItemType } from '../../../ts/ServerApi/types/IFavorite';
import { ProfilePictureStyles } from '../../content/styles/ProfilePictureStyles';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { SharedViewsButton } from '../SharedWithMe/SharedViewsButton';
import { SharedViewsButtonStyle } from '../SharedWithMe/style/SharedViewsButtonStyle';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { User } from '../Tooltips/Elements/User';
import { SharedWithMeTooltip } from '../Tooltips/SharedWithMeTooltip';
import { TooltipAnchor } from '../Tooltips/TooltipAnchor';
import { CardViewStyles } from './CardViewStyles';
import { renderCollectionCardContent } from './CollectionCard';
import { ContentCard, ContentCardProps, getContentUrlForItem } from './ContentCard';
import { DatasourceDefaultImageUrl, hasExtraDatasourceDetail, detailedDatasourceThumbnail } from './DatasourceCard';
import { DetailsTooltipStyles } from './DetailsTooltipStyles';
import { FlowDefaultImageUrl } from './FlowCard';
import { getAssignedThumbnailImgPath, getThumbnailUrlFromDescription, renderProjectCardOverlay } from './ProjectCard';
import { PublishedConnectionDefaultImageUrl } from './PublishedConnectionCard';
import { Thumbnail } from './Thumbnail';
import { ViewDefaultImageUrl } from './ViewCard';
import { WorkbookDefaultImageUrl } from './WorkbookCard';

// MetricCardStateProps contains a metricData property and is made optional because
// SharedWithMeCard can be used for different content types. MetricCardStateProps
// will be defined only when SharedWithMeCard is instantiated for a metric.
export type SharedWithMeCardProps = ContentCardProps & Partial<MetricCardStateProps>;

export class SharedWithMeCard extends ContentCard<SharedWithMeCardProps> {
  public static readonly SHARED_AT_TEST_ID: string = 'sharedAt';
  public static readonly SHARER_TEST_ID: string = 'sharer';
  public static readonly CARD_CONTENT_TEST_ID: string = 'shared-with-me-card-content';

  public render() {
    const content = this.props.content as ISharedWithMeContentInfo;
    return (
      <div className={CardViewStyles.Card} data-tb-test-id={SharedWithMeCard.CARD_CONTENT_TEST_ID}>
        { content.contentType === 'project' && renderProjectCardOverlay() }
        <TooltipAnchor
          tooltipContent={this.renderTooltipContent}
          width={DetailsTooltipStyles.Width}
          title={Messages.shareDetails()}
          disableTooltip={this.props.disableTooltip || content.isAccessDenied}
        >
          { this.renderThumbnail(content) }
        </TooltipAnchor>
        <div className={CardViewStyles.ActionRow}>
          <span className={CardViewStyles.ActionRowName} title={content.name}>
            { content.name }
          </span>
          { !content.isAccessDenied &&
            <>
            { this.hasSharedViews(content.isShareActivityIconVisible) &&
              <span>
                <SharedViewsButton className={SharedViewsButtonStyle.Card} urls={this.props.urls} viewId={content.id} />
              </span>
            }
            {(this.props.content.contentType.toLowerCase() !== 'collection' || FeatureFlags.useCollectionsFavorites) &&
              <span>
                <FavoriteToggle
                  isTabbable={true}
                  isFavorite={content.favorite}
                  onAddFavorite={() => this.props.addContentFavorite({ ...content, contentType: content.contentType as FavoriteItemType })}
                  onRemoveFavorite={() => this.props.removeContentFavorite({ ...content, contentType: content.contentType as FavoriteItemType })}
                />
              </span>
            }

            { this.renderActionsMenu() }
            </>
          }
        </div>
        <div className={CardViewStyles.SharedWithMeContent}>
          { renderSharerProfilePicture(content.sharer) }
          <div className={CardViewStyles.SharedWithMeSharerAndText}>
            <div>
              { renderSharerNameWithMessage(
                  /** sharer */ content.sharer,
                  /** urls */ this.props.urls,
                  /** messageKey */ 'shared',
                  /** shownBelowThumbnail */ true,
                  /** cv */ content.customView,
                )
              }
            </div>
            { content.sharedAt &&
              <div data-tb-test-id={SharedWithMeCard.SHARED_AT_TEST_ID}>
                { Formatters.formatLongBestFitRelativeTime(content.sharedAt) }
              </div>
            }
          </div>
        </div>
      </div>
    );
  }

  private hasSharedViews = (hasSharedViews: boolean): boolean => {
    return FeatureFlags.sharedWithMeForCustomViewEnabled && hasSharedViews;
  }

  private renderTooltipContent = () => {
    return <SharedWithMeTooltip content={this.props.content} urls={this.props.urls} />;
  }

  private getCollectionVisilibityFromContentDetails = (content: ISharedWithMeContentInfo): string => {
    if (content.contentType.toLowerCase() === 'collection' && content.contentDetail) {
      return content.contentDetail.visibility || null;
    }
  }

  private renderThumbnail = (content: ISharedWithMeContentInfo) => {
    const isAccessDenied = content.isAccessDenied;
    const ariaLabel = this.thumbnailAltText(isAccessDenied);
    if (!isAccessDenied) {
      // metrics thumbnail is a different component
      if (!_.isUndefined(this.props.metricData) && !_.isUndefined(this.props.metricData.data)) {
        return renderMetricCardContent(this.props.metricData, this.props);
      }

      // connected datasource thumbnail is a different component
      if (hasExtraDatasourceDetail(content)) {
        return detailedDatasourceThumbnail(getContentUrlForItem(content, this.props.urls), this.props, ariaLabel);
      }

      if (content.contentType.toLowerCase() === 'collection') {
        // Note: Collection has visibility property (see ICollectionInfo), i.e. content.visibility
        // while it is not available in other content types (see IContentInfo), i.e. content.contentDetails
        // As a workaround for SharedWithMe, getSharedItems API add visibility to contentDetails
        let collectionCardProps: any = this.props;
        collectionCardProps.content.visibility = this.getCollectionVisilibityFromContentDetails(content);
        return renderCollectionCardContent(collectionCardProps);
      }
    }

    return (
      <Thumbnail
        allowDragAndDrop={this.props.allowDragAndDrop}
        ariaLabel={ariaLabel}
        centeredText={isAccessDenied ? Messages.permissionsRequiredLabel() : undefined}
        decorate={content.contentType === 'workbook'}
        defaultImageUrl={isAccessDenied ? '' : this.mapContentTypeToDefaultImageUrl()}
        hoveredOrSelected={this.props.hoveredOrSelected}
        imageCoverBackground={content.contentType === 'project'}
        // if the content has an associated custom view, link to the custom view url instead
        navigateUrl={content.customView ? content.customView.url : getContentUrlForItem(content, this.props.urls)}
        thumbnailImageUrl={isAccessDenied ? '' : this.thumbnailImageUrl()}
        useBase64EncodedImage={this.useBase64EncodedImage()}
      />
    );
  }

  private mapContentTypeToDefaultImageUrl = (): string => {
    switch (this.props.content.contentType.toLowerCase()) {
      case 'view':
        return ViewDefaultImageUrl;
      case 'project':
        return getAssignedThumbnailImgPath(this.props.content.id);
      case 'workbook':
        return WorkbookDefaultImageUrl;
      case 'datasource':
        return DatasourceDefaultImageUrl;
      case 'flow':
        return FlowDefaultImageUrl;
      case 'metric':
        return MetricDefaultImageUrl;
      case 'publishedconnection':
        return PublishedConnectionDefaultImageUrl;
      default:
        return '';
    }
  }

  private thumbnailImageUrl = (): string => {
    switch (this.props.content.contentType.toLowerCase()) {
      case 'project':
        return getThumbnailUrlFromDescription(this.props.content.description);
      case 'view':
      case 'workbook':
      case 'datasource':
      case 'flow':
      case 'metric':
      case 'publishedconnection':
      default:
        return this.props.content.thumbnailUrl;
    }
  }

  private thumbnailAltText = (isAccessDenied: boolean): string => {
    const message = isAccessDenied ? Messages.sharedWithMeThumbnailAltTextForAccessDenied : Messages.sharedWithMeThumbnailAltText;
    const contentName = this.props.content.name;
    switch (this.props.content.contentType.toLowerCase()) {
      case 'project':
        return message({ CONTENT_TYPE: Messages.project(), CONTENT_NAME: contentName });
      case 'view':
        return message({ CONTENT_TYPE: Messages.view(), CONTENT_NAME: contentName });
      case 'workbook':
        return message({ CONTENT_TYPE: Messages.workbook(), CONTENT_NAME: contentName });
      case 'datasource':
        return message({ CONTENT_TYPE: Messages.datasource(), CONTENT_NAME: contentName });
      case 'flow':
        return message({ CONTENT_TYPE: Messages.flow(), CONTENT_NAME: contentName });
      case 'metric':
        return message({ CONTENT_TYPE: Messages.metric(), CONTENT_NAME: contentName });
      case 'publishedconnection':
        return message({ CONTENT_TYPE: Messages.publishedConnection(), CONTENT_NAME: contentName });
      default:
        return '';
    }
  }

  private useBase64EncodedImage = (): boolean => {
    switch (this.props.content.contentType.toLowerCase()) {
    case 'flow':
      return true;
    case 'project':
    case 'view':
    case 'workbook':
    case 'datasource':
    case 'metric':
    case 'publishedconnection':
    default:
      return false;
    }
  }
}

export function renderSharerProfilePicture(sharer: ISharerInfo) {
  return (
    <div className={CardViewStyles.SharedWithMeProfilePicture}>
      <ProfilePicture
        contentLetters={Helpers.generateAbbrevFromName(sharer.displayName)}
        imageUrl={sharer.isDeleted ? '' : sharer.userImageUrl}
        sizeInPixels={34}
        fontSizeInPixels={ProfilePictureStyles.FontSizes.ExtraSmallIconFontSize}
        shape={ProfilePicture.Shape.Circle} />
    </div>
  );
}

export function renderSharerNameWithMessage(
  sharer: ISharerInfo,
  urls: SpacesSiteUrls,
  messageKey: string,
  shownBelowThumbnail: boolean,
  cv?: ISharedWithMeCustomViewInfo,
) {
  const translate = ServiceLocator.get<ng.translate.ITranslateService>('$translate');
  const sharerName = sharer.isDeleted ? Messages.sharedWithMeRemovedUser({ USERNAME: sharer.username }) : sharer.displayName;
  const title = shownBelowThumbnail ? sharerName : undefined;

  let renderSharerName: () => JSX.Element;
  if (shownBelowThumbnail && !sharer.isDeleted) {
    renderSharerName = () => (
      <TextLinkWidget
        href={urls.userUrl(sharer.username, sharer.domainName)}
        testId={SharedWithMeCard.SHARER_TEST_ID}
        title={title}
      >
        {sharerName}
      </TextLinkWidget>
    );
  } else {
    renderSharerName = () => (
      <span
        data-tb-test-id={SharedWithMeCard.SHARER_TEST_ID}
        title={title}
      >
        {sharer.isDeleted ? sharerName : <User user={sharer} urls={urls} />}
      </span>
    );
  }

  return (
    <InterpolatedString
      baseStringKey={messageKey}
      translate={translate.instant}
      replacements={{
        USER_NAME: renderSharerName,
        ...(cv && !cv.isHidden) ? {
          VIEW_NAME: () => <>{cv.name}</>
        } : {}
      }}
      containerClassName={shownBelowThumbnail ? CardViewStyles.SharedWithMeSingleLine : undefined}
      replacementPartClassName={shownBelowThumbnail ? CardViewStyles.SharedWithMeSharerNameSingleLine : undefined}
    />
  );
}

const mapStateToProps: MapStateToProps<MetricCardStateProps> = (state: AppState, ownProps: ContentCardProps) => {
  const metricData = ownProps.content.contentType === 'metric' ? selectMetricDataForContentItem(state, ownProps.content) : undefined;
  return { metricData };
};

export const ConnectedSharedWithMeCard: React.ComponentClass<ContentCardProps> = connect(mapStateToProps, undefined)(SharedWithMeCard);
